import { useNavigation } from '@react-navigation/native'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FlatList,
  ListRenderItemInfo,
  Pressable,
  StyleSheet,
  View,
} from 'react-native'
import { Text } from 'react-native-paper'
import { ScreenWrapper } from '../../../../@ui/containers/grid/screen.wrapper'
import {
  ContributionStatus,
  ContributionType,
} from '../../../../generated/graphql'
import { useTheme } from '../../../../hooks/theme.hook'
import { Contribution } from '../types'
import { ContributionCard } from './contribution.card'
import ContributionStatusSelector from './contribution-status.selector'
import ContributionTypeSelector from './contribution.type.selector'
import { createStyle } from '../../../../contexts/global-styles.provider'

interface ContributionListProps {
  contributions: Contribution[]
  type: ContributionType | 'All'
  status: ContributionStatus | 'All'
  loading?: boolean
  hideFilters?: boolean
  readonly?: boolean
  onContributionPress?: (contribution: Contribution) => void
  onFilterChange?: (
    type: ContributionType | 'All',
    status: ContributionStatus | 'All'
  ) => void
}

export function ContributionList(props: ContributionListProps) {
  const { contributions, onContributionPress } = props
  const theme = useTheme()
  const styles = useStyle()
  const { t } = useTranslation('contributions')

  const renderItem = ({ item }: ListRenderItemInfo<Contribution>) => {
    return (
      <Pressable onPress={() => onContributionPress?.(item)}>
        <ContributionCard
          key={item.id}
          contribution={item}
          readonly={props.readonly}
        />
      </Pressable>
    )
  }

  return (
    <ScreenWrapper withScrollView={false} style={{ paddingBottom: 0 }}>
      <View style={styles.list}>
        {!props.hideFilters && (
          <View style={styles.menuRow}>
            <View style={styles.typeMenu}>
              <Text>{t('Status')}</Text>
              <ContributionStatusSelector
                onSelect={(value) => props.onFilterChange?.(props.type, value)}
                selected={props.status}
              />
            </View>
            <View style={styles.typeMenu}>
              <Text>{t('Type')}</Text>
              <ContributionTypeSelector
                onSelect={(value) =>
                  props.onFilterChange?.(value, props.status)
                }
                selected={props.type}
              />
            </View>
          </View>
        )}
        {contributions.length > 0 ? (
          <FlatList
            style={{ paddingHorizontal: theme.spacing(5) }}
            data={contributions}
            renderItem={renderItem}
            keyExtractor={(item) => item.id}
            ListFooterComponent={<View style={{ height: 200 }}></View>}
          />
        ) : (
          <View style={styles.empty}>
            <Text
              variant={'titleMedium'}
              style={{ color: theme.palettes.neutral[70]! }}
            >
              {t('No contributions')}
            </Text>
          </View>
        )}
      </View>
    </ScreenWrapper>
  )
}

const useStyle = createStyle(({ theme }) => ({
  empty: {
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  list: {
    marginTop: theme.spacing(5),
    flex: 1,
  },
  menuRow: {
    marginBottom: theme.spacing(3),
    marginHorizontal: theme.spacing(5),
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
  },
  sortBy: {},
  typeMenu: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    columnGap: theme.spacing(2),
  },
}))
